import { ExternalEventService } from '@base/services/external-event.service';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { TypeOfRequestLabel, TypeOfOperation } from '@base/enums/request-label.enum';

export interface InformaMfeNavigateValue {
  requestId: string;
  target:
    | 'CONCIERGE'
    | 'MEETING_REQUEST'
    | 'EXHIBITOR'
    | 'PRODUCT'
    | 'AGENDA_SESSION'
    | 'PEOPLE'
    | 'DIRECTORY'
    | 'BOOKMARKS'
    | 'MY_SCHEDULE'
    | 'REQUEST_SENT';
  entityId?: string;
  entityType?: string;
  context?: InformaMfeNavigateContext;
}

export interface InformaMfeMatchContext {
  readonly matchReason?: string;
  readonly matchReasons?: readonly string[];
}

export interface InformaMfeOverlayFeedback {
  action: 'NOT_RELEVANT';
  entityType?: string;
  entityId: string;
  requestId?: string;
  at: number;
}

export interface InformaMfeNavigateContext extends InformaMfeMatchContext {
  readonly productId?: string;
  readonly prompt?: string;
  readonly source?: string;
  readonly entityType?: string;
  readonly sessionId?: string;
  readonly entityName?: string;
}

export const INFORMA_TARGET_ENTITY: Partial<Record<InformaMfeNavigateValue['target'], string>> = {
  PEOPLE: 'PEOPLE',
  EXHIBITOR: 'COMPANY',
  PRODUCT: 'PRODUCT',
  AGENDA_SESSION: 'AGENDA',
};

export type InformaMfeNavigateOutcome = 'OPENED' | 'COMPLETED' | 'DISMISSED' | 'FAILED';

/** Reply payload set on the MFE element via the `navigate-result` attribute. */
export interface InformaMfeNavigateResult {
  requestId: string;
  outcome: InformaMfeNavigateOutcome;
  errorCode?: string;
}

/** Maps Informa entityType strings to the corresponding Jublia bookmark label. */
export const INFORMA_BOOKMARK_ACTION: Partial<Record<string, TypeOfRequestLabel>> = {
  PEOPLE: TypeOfRequestLabel.ATTENDEE_BOOKMARK,
  EXHIBITOR: TypeOfRequestLabel.COMPANY_BOOKMARK,
  PRODUCT: TypeOfRequestLabel.PRODUCT_BOOKMARK,
  AGENDA_SESSION: TypeOfRequestLabel.AGENDA_BOOKMARK,
};

/** Parsed and validated shape of a `USER_ACTION` event payload. */
export interface UserActionPayload {
  action: string;
  entityType: string;
  entityId: string;
  saved?: boolean;
}

/** Services and config injected by the host component into each USER_ACTION handler. */
export interface InformaMfeUserActionContext {
  localBookmarks: Set<string>;
  externalEventSrv: ExternalEventService;
  requestLabelSrv: RequestLabelService;
  logPrefix: string;
}

/** Resolved bookmark data required to persist a single bookmark change. */
export interface BookmarkData {
  translateEntity: string;
  entityId: string;
  bookmarkAction: TypeOfRequestLabel;
  operation: TypeOfOperation;
}

/** Attribute value passed to Informa MFEs to seed bookmark/meeting/recommendation highlights. */
export interface InformaMfeUserContext {
  bookmarkedSfIds: string[];
  meetingSfIds: string[];
  recommendedSfIds: string[];
}

export interface InformaMfeEvent {
  type:
    | 'READY'
    | 'LOADING'
    | 'AUTH_REQUIRED'
    | 'NAVIGATE'
    | 'NATIVE_CAPABILITY_REQUEST'
    | 'USER_ACTION'
    | 'ERROR'
    | 'CLOSED';
  status: 'SUCCESS' | 'FAILED' | 'INFO';
  value?: InformaMfeNavigateValue | Record<string, unknown>;
  message?: string;
}
